import { useMemo } from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material";
import { FixedSizeList as List } from "react-window";
import clsx from "clsx";
import { useTreeHelpers } from "hooks/useTreeHelpers";
import TreeNode, { treeNodePropType } from "./TreeNode";

const ListStyled = styled(List)({
  width: "100%",
});

const flattenTree = (nodes) => {
  return nodes.reduce((acc, node) => {
    acc.push({ ...node });
    if (node.children && node.isExpanded) {
      acc = acc.concat(flattenTree(node.children));
    }
    return acc;
  }, []);
};

const Tree = ({
  data,
  height,
  itemSize = 30,
  onChange,
  checkedNodes = [],
  isDisabled,
  className,
  classNames = {}, //Expected classNames: nodeWrapper,node,nodeButton,nodeInput,nodeText
}) => {
  const { nodes, handleCheck, handleToggle } = useTreeHelpers({
    data,
    checkedNodes,
    onChange,
  });

  // Memoize flattened tree structure
  const flattenedNodes = useMemo(() => flattenTree(nodes), [nodes]);

  const finalHeight = useMemo(() => {
    if (height) {
      return Math.min(flattenedNodes.length * itemSize, height);
    }
    return flattenedNodes.length * itemSize;
  }, [flattenedNodes.length, height, itemSize]);

  const { nodeWrapper, ...nodeClassNames } = classNames;

  return (
    <ListStyled
      height={finalHeight}
      itemSize={itemSize}
      itemCount={flattenedNodes.length}
      className={clsx("tree-list", className)}
    >
      {({ index, style }) => {
        const node = flattenedNodes[index];
        return (
          <div className={nodeWrapper} style={style}>
            <TreeNode
              isDisabled={isDisabled}
              node={node}
              onToggle={handleToggle}
              onCheck={handleCheck}
              isChecked={checkedNodes
                .map((node) => node.value)
                .includes(node.value)}
              classNames={nodeClassNames}
            />
          </div>
        );
      }}
    </ListStyled>
  );
};

export default Tree;

Tree.propTypes = {
  checkedNodes: PropTypes.arrayOf(PropTypes.object),
  data: PropTypes.arrayOf(PropTypes.shape(treeNodePropType)).isRequired,
  height: PropTypes.number,
  itemSize: PropTypes.number,
  onChange: PropTypes.func,
  className: PropTypes.string,
  isDisabled: PropTypes.bool,
  style: PropTypes.object,
};
